/**
 * Player skin + cosmetics: the persisted look of the ragdoll stickman.
 *
 * A "skin" is a preset palette (limb / torso / head / outline colors).
 * "Cosmetics" are independent slot overlays (hat, gloves, boots) that
 * combine with any skin. Both are persisted to localStorage and applied
 * to the live ragdoll on every level rebuild + every customize action.
 *
 * Pure data + a tiny storage shim — no Pixi/DOM imports. Rendering of
 * skins/cosmetics is owned by `Ragdoll.applySkin()`.
 */

export interface SkinColors {
  /** Arms + legs limb fill. */
  limb: number;
  /** Torso (shirt) fill. */
  torso: number;
  /** Head circle fill (skin tone). */
  skin: number;
  /** Outline color used for every body part. */
  stroke: number;
}

export type HatId = 'tophat' | 'cap' | 'beanie' | 'crown' | 'horns';
export type GlovesId = 'red' | 'white' | 'black' | 'gold';
export type BootsId = 'brown' | 'red' | 'black' | 'gold';

export interface Cosmetics {
  hat: HatId | null;
  gloves: GlovesId | null;
  boots: BootsId | null;
}

export interface SkinPreset {
  id: string;
  name: string;
  colors: SkinColors;
}

/** Resolved look = preset palette + chosen cosmetics. */
export interface ActiveSkin {
  preset: SkinPreset;
  cosmetics: Cosmetics;
}

/** Built-in skin palettes. The first entry is the default. The 'custom'
 *  entry is special: its colors are read from the user's stored custom
 *  palette (mutable via `setCustomColor`) so the picker can edit them
 *  in place. */
export const SKIN_PRESETS: ReadonlyArray<SkinPreset> = [
  { id: 'classic',  name: 'Classic',  colors: { limb: 0x2c66c9, torso: 0x2c66c9, skin: 0xffe9c4, stroke: 0x0a1a3a } },
  { id: 'crimson',  name: 'Crimson',  colors: { limb: 0xc92c46, torso: 0x7a1a2a, skin: 0xffe9c4, stroke: 0x2a0a0f } },
  { id: 'forest',   name: 'Forest',   colors: { limb: 0x2c8a3a, torso: 0x1f5e29, skin: 0xefd1a8, stroke: 0x0a2010 } },
  { id: 'midnight', name: 'Midnight', colors: { limb: 0x2a2a55, torso: 0x101030, skin: 0xc8a987, stroke: 0x000010 } },
  { id: 'sunset',   name: 'Sunset',   colors: { limb: 0xff8a3c, torso: 0xc9472c, skin: 0xffe9c4, stroke: 0x3a1408 } },
  { id: 'stealth',  name: 'Stealth',  colors: { limb: 0x222222, torso: 0x111111, skin: 0xb89070, stroke: 0x000000 } },
  { id: 'frost',    name: 'Frost',    colors: { limb: 0x9ad6ff, torso: 0x4f9fd6, skin: 0xfff0d8, stroke: 0x0a3050 } },
  { id: 'royal',    name: 'Royal',    colors: { limb: 0x6633aa, torso: 0x3a1a66, skin: 0xffe9c4, stroke: 0x1a0030 } },
  { id: 'custom',   name: 'Custom',   colors: { limb: 0x2c66c9, torso: 0x2c66c9, skin: 0xffe9c4, stroke: 0x0a1a3a } },
];

export const CUSTOM_PRESET_ID = 'custom';
const DEFAULT_CUSTOM_COLORS: SkinColors = { limb: 0x2c66c9, torso: 0x2c66c9, skin: 0xffe9c4, stroke: 0x0a1a3a };

export const HAT_OPTIONS: ReadonlyArray<{ id: HatId | null; name: string }> = [
  { id: null,      name: 'None' },
  { id: 'cap',     name: 'Cap' },
  { id: 'beanie',  name: 'Beanie' },
  { id: 'tophat',  name: 'Top hat' },
  { id: 'crown',   name: 'Crown' },
  { id: 'horns',   name: 'Horns' },
];

export const GLOVES_OPTIONS: ReadonlyArray<{ id: GlovesId | null; name: string }> = [
  { id: null,    name: 'None' },
  { id: 'red',   name: 'Red' },
  { id: 'white', name: 'White' },
  { id: 'black', name: 'Black' },
  { id: 'gold',  name: 'Gold' },
];

export const BOOTS_OPTIONS: ReadonlyArray<{ id: BootsId | null; name: string }> = [
  { id: null,    name: 'None' },
  { id: 'brown', name: 'Brown' },
  { id: 'red',   name: 'Red' },
  { id: 'black', name: 'Black' },
  { id: 'gold',  name: 'Gold' },
];

const KEY = 'shk.skin.v1';
const DEFAULT_PRESET_ID = 'classic';
const DEFAULT_COSMETICS: Cosmetics = { hat: null, gloves: null, boots: null };

interface StoredPrefs {
  skinId: string;
  cosmetics: Cosmetics;
  /** User-edited palette used when skinId === 'custom'. Persists across
   *  preset switches so coming back to Custom restores prior choices. */
  custom?: SkinColors;
}

function readPrefs(): StoredPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { skinId: DEFAULT_PRESET_ID, cosmetics: { ...DEFAULT_COSMETICS }, custom: { ...DEFAULT_CUSTOM_COLORS } };
    const parsed = JSON.parse(raw) as Partial<StoredPrefs>;
    const skinId = typeof parsed.skinId === 'string' && SKIN_PRESETS.some(p => p.id === parsed.skinId)
      ? parsed.skinId : DEFAULT_PRESET_ID;
    const c = (parsed.cosmetics ?? {}) as Partial<Cosmetics>;
    const hat = isHat(c.hat) ? c.hat : null;
    const gloves = isGloves(c.gloves) ? c.gloves : null;
    const boots = isBoots(c.boots) ? c.boots : null;
    const custom = sanitizeColors(parsed.custom);
    return { skinId, cosmetics: { hat, gloves, boots }, custom };
  } catch {
    return { skinId: DEFAULT_PRESET_ID, cosmetics: { ...DEFAULT_COSMETICS }, custom: { ...DEFAULT_CUSTOM_COLORS } };
  }
}

function sanitizeColors(c: unknown): SkinColors {
  if (!c || typeof c !== 'object') return { ...DEFAULT_CUSTOM_COLORS };
  const obj = c as Partial<Record<keyof SkinColors, unknown>>;
  const pick = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 0xffffff ? Math.floor(v) : fallback;
  return {
    limb:   pick(obj.limb,   DEFAULT_CUSTOM_COLORS.limb),
    torso:  pick(obj.torso,  DEFAULT_CUSTOM_COLORS.torso),
    skin:   pick(obj.skin,   DEFAULT_CUSTOM_COLORS.skin),
    stroke: pick(obj.stroke, DEFAULT_CUSTOM_COLORS.stroke),
  };
}

function writePrefs(prefs: StoredPrefs): void {
  try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* quota: silent */ }
}

function isHat(v: unknown): v is HatId {
  return v === 'tophat' || v === 'cap' || v === 'beanie' || v === 'crown' || v === 'horns';
}
function isGloves(v: unknown): v is GlovesId {
  return v === 'red' || v === 'white' || v === 'black' || v === 'gold';
}
function isBoots(v: unknown): v is BootsId {
  return v === 'brown' || v === 'red' || v === 'black' || v === 'gold';
}

function presetById(id: string): SkinPreset {
  return SKIN_PRESETS.find(p => p.id === id) ?? SKIN_PRESETS[0]!;
}

/** Active resolved skin = preset palette + chosen cosmetics.
 *  When the active id is 'custom', returns the user's edited colors. */
export function getActiveSkin(): ActiveSkin {
  const p = readPrefs();
  const preset = presetById(p.skinId);
  if (preset.id === CUSTOM_PRESET_ID) {
    return { preset: { ...preset, colors: p.custom ?? { ...DEFAULT_CUSTOM_COLORS } }, cosmetics: p.cosmetics };
  }
  return { preset, cosmetics: p.cosmetics };
}

/** Read-only access to the user's custom palette (for the picker UI). */
export function getCustomColors(): SkinColors {
  return readPrefs().custom ?? { ...DEFAULT_CUSTOM_COLORS };
}

/** Update one slot of the custom palette. Implies the active id stays
 *  whatever it was; UI typically calls setActiveSkinId('custom') first. */
export function setCustomColor(slot: keyof SkinColors, value: number): void {
  const prefs = readPrefs();
  const current = prefs.custom ?? { ...DEFAULT_CUSTOM_COLORS };
  prefs.custom = { ...current, [slot]: value };
  writePrefs(prefs);
}

export function getActiveSkinId(): string {
  return readPrefs().skinId;
}

export function setActiveSkinId(id: string): void {
  const prefs = readPrefs();
  prefs.skinId = SKIN_PRESETS.some(p => p.id === id) ? id : DEFAULT_PRESET_ID;
  writePrefs(prefs);
}

export function setCosmetic<K extends keyof Cosmetics>(slot: K, value: Cosmetics[K]): void {
  const prefs = readPrefs();
  prefs.cosmetics = { ...prefs.cosmetics, [slot]: value };
  writePrefs(prefs);
}

/** Look up a preset by id (falls back to the default). */
export function getPreset(id: string): SkinPreset {
  return presetById(id);
}

/** Color palette helpers for cosmetics — small lookup tables so the
 *  renderer doesn't bake palette knowledge into draw code. */
export const HAT_COLORS: Record<HatId, { fill: number; band?: number }> = {
  cap:    { fill: 0xc94040, band: 0x222222 },
  beanie: { fill: 0x4080ff, band: 0xffffff },
  tophat: { fill: 0x111111, band: 0xb83333 },
  crown:  { fill: 0xffd233, band: 0xff6633 },
  horns:  { fill: 0xefe2c8 },
};

export const GLOVES_COLORS: Record<GlovesId, number> = {
  red:   0xc94040,
  white: 0xeeeeee,
  black: 0x111111,
  gold:  0xffd233,
};

export const BOOTS_COLORS: Record<BootsId, number> = {
  brown: 0x6a3a1a,
  red:   0xc94040,
  black: 0x111111,
  gold:  0xffd233,
};
